import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Calculator, PenTool, Globe, Play, Star } from 'lucide-react';
import { ModuleData, ModuleProgress, ModuleId } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { getModuleDisplayInfo } from '../../config/moduleContent';
import { useAppStore } from '../../hooks/useAppStore';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { PageHeader } from '../shared/PageHeader';
import * as Icons from 'lucide-react';
// Subject category groupings
const SUBJECT_GROUPS: {
  label: string;
  emoji: string;
  moduleIds: ModuleId[];
}[] = [{
  label: 'Rekenen',
  emoji: '📐',
  moduleIds: ['Getalbegrip', 'Metriek', 'TijdGeld']
}, {
  label: 'Taal',
  emoji: '📝',
  moduleIds: ['SpellingWW', 'SpellingRegel', 'Taalbegrip']
}, {
  label: 'Wereld & Studie',
  emoji: '🌍',
  moduleIds: ['Studievaardig', 'Topo']
}];
interface SubjectsPageProps {
  modules: ModuleData[];
  progress: Record<ModuleId, ModuleProgress>;
  selectedGroup: GroupId;
  onModuleSelect: (module: ModuleData) => void;
  onStartPractice: (moduleId: ModuleId) => void;
}
export function SubjectsPageRedesigned({
  modules,
  progress,
  selectedGroup,
  onModuleSelect,
  onStartPractice
}: SubjectsPageProps) {
  const moduleById = Object.fromEntries(modules.map(m => [m.id, m]));
  return <div className="pb-20 space-y-8" data-id="element-5146">
      {/* Header */}
      <PageHeader icon={BookOpen} title="Mijn Vakken" subtitle="Kies een onderwerp om te oefenen" badge={`Groep ${selectedGroup}`} accentColor="bg-indigo-100 text-indigo-600" data-id="element-5147" />

      {/* Grouped Subjects */}
      {SUBJECT_GROUPS.map((group, groupIndex) => {
      const groupModules = group.moduleIds.map(id => moduleById[id]).filter((m): m is ModuleData => !!m);
      if (groupModules.length === 0) return null;
      return <motion.div key={group.label} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: groupIndex * 0.1,
        duration: 0.4
      }} data-id="element-5148">
            {/* Group Header */}
            <div className="flex items-center gap-3 mb-4" data-id="element-5149">
              <span className="text-2xl" data-id="element-5150">{group.emoji}</span>
              <h2 className="text-lg font-bold text-slate-900" data-id="element-5151">
                {group.label}
              </h2>
              <div className="flex-1 h-px bg-slate-200" data-id="element-5152" />
            </div>

            {/* Group Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5153">
              {groupModules.map((module, index) => <SubjectCardClean key={module.id} module={module} selectedGroup={selectedGroup} index={index} groupIndex={groupIndex} onClick={() => onModuleSelect(module)} onStart={() => onStartPractice(module.id)} data-id="element-5154" />)}
            </div>
          </motion.div>;
    })}
    </div>;
}
// Clean Subject Card - Minimal stats, workbook feel
function SubjectCardClean({
  module,
  selectedGroup,
  index,
  groupIndex,
  onClick,
  onStart
}: {
  module: ModuleData;
  selectedGroup: GroupId;
  index: number;
  groupIndex: number;
  onClick: () => void;
  onStart: () => void;
}) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const IconComponent = (Icons as any)[module.icon] || Icons.BookOpen;
  const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
  const title = displayInfo?.title || module.title;
  const description = displayInfo?.description || module.description;
  const mastery = getModuleMasteryForGroup(module.id);
  const answered = getAnsweredQuestions(module.id).length;
  const isStarted = answered > 0 || mastery > 0;
  const isMastered = mastery >= 95;
  const colorMap: Record<string, {
    bg: string;
    icon: string;
    border: string;
    solid: string;
  }> = {
    'text-blue-500': {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      border: 'border-blue-200',
      solid: 'bg-blue-500'
    },
    'text-indigo-500': {
      bg: 'bg-indigo-50',
      icon: 'text-indigo-600',
      border: 'border-indigo-200',
      solid: 'bg-indigo-500'
    },
    'text-emerald-500': {
      bg: 'bg-emerald-50',
      icon: 'text-emerald-600',
      border: 'border-emerald-200',
      solid: 'bg-emerald-500'
    },
    'text-rose-500': {
      bg: 'bg-rose-50',
      icon: 'text-rose-600',
      border: 'border-rose-200',
      solid: 'bg-rose-500'
    },
    'text-amber-500': {
      bg: 'bg-amber-50',
      icon: 'text-amber-600',
      border: 'border-amber-200',
      solid: 'bg-amber-500'
    },
    'text-purple-500': {
      bg: 'bg-purple-50',
      icon: 'text-purple-600',
      border: 'border-purple-200',
      solid: 'bg-purple-500'
    },
    'text-teal-500': {
      bg: 'bg-teal-50',
      icon: 'text-teal-600',
      border: 'border-teal-200',
      solid: 'bg-teal-500'
    },
    'text-cyan-500': {
      bg: 'bg-cyan-50',
      icon: 'text-cyan-600',
      border: 'border-cyan-200',
      solid: 'bg-cyan-500'
    }
  };
  const colors = colorMap[module.color] || {
    bg: 'bg-slate-50',
    icon: 'text-slate-600',
    border: 'border-slate-200',
    solid: 'bg-slate-500'
  };
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: groupIndex * 0.1 + index * 0.05,
    duration: 0.4
  }} className="h-full" data-id="element-5155">
      <Card className={`h-full flex flex-col p-0 overflow-hidden cursor-pointer group transition-all hover:shadow-lg hover:-translate-y-1 ${isMastered ? 'border-emerald-300 ring-2 ring-emerald-100' : `border ${colors.border}`}`} onClick={onClick} data-id="element-5156">
        {/* Header with icon */}
        <div className={`${colors.bg} p-5 relative`} data-id="element-5157">
          {isMastered && <div className="absolute top-3 right-3" data-id="element-5158">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-md" data-id="element-5159">
                <Star className="w-4 h-4 text-white fill-current" data-id="element-5160" />
              </div>
            </div>}
          <div className={`w-14 h-14 ${colors.solid} rounded-2xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`} data-id="element-5161">
            <IconComponent className="w-7 h-7 text-white" strokeWidth={2.5} data-id="element-5162" />
          </div>
        </div>

        {/* Content */}
        <div className="p-5 flex-1 flex flex-col" data-id="element-5163">
          <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-indigo-700 transition-colors" data-id="element-5164">
            {title}
          </h3>
          <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-2 flex-1" data-id="element-5165">
            {description}
          </p>

          {/* Simple status + action */}
          <div className="flex items-center justify-between" data-id="element-5166">
            <div className="flex items-center gap-2" data-id="element-5167">
              {isMastered ? <span className="text-sm font-semibold text-emerald-600" data-id="element-5168">
                  ✓ Klaar
                </span> : isStarted ? <>
                  <div className="flex gap-0.5" data-id="element-5169">
                    {[0, 1, 2, 3].map(i => <div key={i} className={`w-2 h-2 rounded-full ${i < Math.ceil(mastery / 25) ? colors.solid : 'bg-slate-200'}`} data-id="element-5170" />)}
                  </div>
                  <span className="text-sm font-medium text-slate-500" data-id="element-5171">
                    Bezig
                  </span>
                </> : <span className="text-sm font-medium text-slate-400" data-id="element-5172">
                  Nieuw
                </span>}
            </div>

            <Button size="sm" variant={isStarted ? 'primary' : 'secondary'} onClick={e => {
            e.stopPropagation();
            onStart();
          }} data-id="element-5173">
              {isStarted ? 'Verder' : 'Start'}
              <Play className="w-4 h-4 ml-1 fill-current" data-id="element-5174" />
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>;
}